import { inject, Injectable, signal, untracked } from '@angular/core';
import { ActivityBin, Highlight, TwitchComment } from '../models/twitch.model';
import { TwitchChatStateService } from './twitch-chat-state.service.js';
import { TwitchChatService } from './twitch-chat.service.js';

const BIN_DURATION_SECONDS = 15;

interface DemoStreamFile {
  version: number;
  meta: {
    streamerName: string;
    videoTitle: string;
    videoGame: string;
    videoId: string;
    videoDurationSec: number;
    streamCreatedAt: string;
  };
  comments: TwitchComment[];
}

interface DemoQuickFile {
  version: number;
  byClusterStartTime: Record<string, { description: string; sentiment: string }>;
}

interface DemoDeepFile {
  version: number;
  deepHighlights: Highlight[];
}

interface DemoSearchFile {
  version: number;
  preFilledQuery: string;
}

/** Detect demo mode — pure function so any service can read it without DI. */
export function checkDemoMode(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('demo') === '1';
  } catch {
    return false;
  }
}

@Injectable({ providedIn: 'root' })
export class DemoService {
  private state = inject(TwitchChatStateService);
  private chatService = inject(TwitchChatService);

  isDemoMode = signal<boolean>(checkDemoMode());
  signInPromptVisible = signal<boolean>(false);
  signInPromptReason = signal<string>('');
  hydrationComplete = signal<boolean>(false);

  private quickAi: DemoQuickFile | null = null;
  private deepAi: DemoDeepFile | null = null;
  private searchPrefill: DemoSearchFile | null = null;
  private hydrationPromise: Promise<void> | null = null;

  async hydrateAll(): Promise<void> {
    if (!this.isDemoMode()) return;
    if (this.hydrationPromise) return this.hydrationPromise;

    this.hydrationPromise = this._hydrate();
    return this.hydrationPromise;
  }

  private async _hydrate(): Promise<void> {
    try {
      const [streamRes, quickRes, deepRes, searchRes] = await Promise.all([
        fetch('assets/demo/demo-stream.json'),
        fetch('assets/demo/demo-ai-quick.json'),
        fetch('assets/demo/demo-ai-deep.json'),
        fetch('assets/demo/demo-search.json'),
      ]);

      const stream: DemoStreamFile = await streamRes.json();
      this.quickAi = await quickRes.json();
      this.deepAi = await deepRes.json();
      this.searchPrefill = await searchRes.json();

      this.applyDemoStream(stream);
      this.validateAiCoverage();
      this.hydrationComplete.set(true);
    } catch (err) {
      console.error('[demo] hydration failed', err);
      this.state.error.set('Не удалось загрузить демо-данные. Перезагрузите страницу.');
    }
  }

  /**
   * Set comments + meta + bins, then trigger cluster/highlight recompute via
   * the canonical public method updatePeakMessageThreshold().
   */
  private applyDemoStream(stream: DemoStreamFile): void {
    const { meta, comments } = stream;

    untracked(() => {
      this.state.streamerName.set(meta.streamerName);
      this.state.videoTitle.set(meta.videoTitle);
      this.state.videoGame.set(meta.videoGame);
      this.state.videoId.set(meta.videoId);
      this.state.streamCreatedAt.set(meta.streamCreatedAt);
      this.state.fileName.set('demo-stream.json');
      this.state.chatComments.set(comments);
      this.state.filteredComments.set(comments);

      const { bins, maxActivity } = this.binComments(comments);
      this.state.activityBins.set(bins);
      this.state.maxActivity.set(maxActivity > 0 ? maxActivity : 100);
      this.state.isProcessed.set(true);
    });

    // Now trigger cluster recompute through the public API
    // (this also populates highlights from clusters)
    this.chatService.updatePeakMessageThreshold(this.state.peakMessageThreshold());
  }

  /** Binning mirror of twitch-chat.service.ts:581-601 */
  private binComments(comments: TwitchComment[]): { bins: ActivityBin[]; maxActivity: number } {
    const map = new Map<number, ActivityBin>();
    let maxTime = 0;
    for (const c of comments) {
      const idx = Math.floor(c.content_offset_seconds / BIN_DURATION_SECONDS);
      if (!map.has(idx)) {
        map.set(idx, { startTime: idx * BIN_DURATION_SECONDS, messageCount: 0, messages: [] });
      }
      const bin = map.get(idx)!;
      bin.messageCount++;
      bin.messages.push(`${c.commenter.display_name}: ${c.message.body}`);
      if (c.content_offset_seconds > maxTime) maxTime = c.content_offset_seconds;
    }
    const total = Math.floor(maxTime / BIN_DURATION_SECONDS);
    const bins: ActivityBin[] = [];
    let maxActivity = 0;
    for (let i = 0; i <= total; i++) {
      const bin = map.get(i) || { startTime: i * BIN_DURATION_SECONDS, messageCount: 0, messages: [] };
      bins.push(bin);
      if (bin.messageCount > maxActivity) maxActivity = bin.messageCount;
    }
    return { bins, maxActivity };
  }

  private validateAiCoverage(): void {
    if (!this.quickAi) return;
    const covered = new Set(Object.keys(this.quickAi.byClusterStartTime).map(Number));
    const missing: number[] = [];
    for (const h of this.state.highlights()) {
      if (h.source !== 'peak') continue;
      if (!covered.has(h.startTime)) missing.push(h.startTime);
    }
    if (missing.length > 0) {
      console.warn(`[demo] Quick AI missing descriptions for cluster startTimes: ${missing.join(', ')}. Re-run tools/generate-demo.mjs.`);
    }
  }

  /** Imitates analyzeHighlightsWithAI: 1.2s spinner, then fills sentiment+description on current peak highlights. */
  async simulateQuickAi(): Promise<void> {
    if (!this.quickAi) return;

    const peakHighlights = this.state.highlights().filter(h => !h.isAnalyzed && h.source === 'peak');
    if (peakHighlights.length === 0) return;

    this.state.isAnalyzingQuick.set(true);
    this.state.error.set('');

    await new Promise(r => setTimeout(r, 1200));

    const { byClusterStartTime } = this.quickAi;
    const cache = new Map(this.state.analysisCache());
    for (const h of peakHighlights) {
      const data = byClusterStartTime[String(h.startTime)] ?? byClusterStartTime[h.startTime as any];
      if (data) {
        cache.set(h.startTime, data);
      } else {
        cache.set(h.startTime, {
          description: 'Описание этого момента не предзагружено в демо.',
          sentiment: 'Нейтральный',
        });
      }
    }
    this.state.analysisCache.set(cache);

    this.state.highlights.update(hs => hs.map(h => {
      if (h.source !== 'peak') return h;
      const cached = cache.get(h.startTime);
      if (!cached) return h;
      return { ...h, description: cached.description, sentiment: cached.sentiment, isAnalyzed: true };
    }));

    this.state.isAnalyzingQuick.set(false);
  }

  /** Imitates runDeepAnalysis: 1.5s spinner, adds 1-2 ai-deep-dive highlights. */
  async simulateDeepAi(): Promise<void> {
    if (!this.deepAi) return;

    this.state.isAnalyzingDeep.set(true);
    this.state.error.set('');

    await new Promise(r => setTimeout(r, 1500));

    const existing = this.state.highlights();
    const existingDeepTimes = new Set(
      existing.filter(h => h.source === 'ai-deep-dive').map(h => h.startTime),
    );
    const newDeep = this.deepAi.deepHighlights.filter(h => !existingDeepTimes.has(h.startTime));

    if (newDeep.length > 0) {
      this.state.highlights.set([...existing, ...newDeep]);
    }

    this.state.isAnalyzingDeep.set(false);
  }

  requestSignIn(reason: string): void {
    this.signInPromptReason.set(reason);
    this.signInPromptVisible.set(true);
  }

  dismissSignInPrompt(): void {
    this.signInPromptVisible.set(false);
  }

  getPreFilledSearchQuery(): string {
    return this.searchPrefill?.preFilledQuery ?? '';
  }
}
